import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Mentionable } from '../../lib/comment-types';
import { Button, Textarea } from '../../ui';
import { insertMention, mentionQuery } from './mentions';

const MAX = 5000;

type Props = {
  label: string;
  mentionables: readonly Mentionable[];
  onSubmit: (body: string) => Promise<unknown>;
  onCancel?: () => void;
  initial?: string;
  submitLabel?: string;
  autoFocus?: boolean;
};

/** Plain-text message box: `@` opens the people of the board, Ctrl/⌘+Enter sends, Escape cancels. */
export function Composer({ label, mentionables, onSubmit, onCancel, initial = '', submitLabel, autoFocus }: Props) {
  const { t } = useTranslation();
  const [text, setText] = useState(initial);
  const [caret, setCaret] = useState(initial.length);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);
  const listId = useId();
  const query = mentionQuery(text, caret);
  const options = query === null ? [] : mentionables.filter((m) => m.displayName.toLowerCase().includes(query.toLowerCase())).slice(0, 8);

  const pick = (m: Mentionable) => {
    const next = insertMention(text, caret, m);
    setText(next.text);
    setCaret(next.caret);
    setActive(0);
    requestAnimationFrame(() => box.current?.setSelectionRange(next.caret, next.caret));
  };
  const submit = async () => {
    const body = text.trim();
    if (!body || body.length > MAX || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      await onSubmit(body);
      setText('');
      setCaret(0);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    e.stopPropagation(); // the canvas shortcuts never see what is typed here
    if (options.length) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((i) => (i + (e.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pick(options[Math.min(active, options.length - 1)]!);
        return;
      }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void submit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel?.();
    }
  };

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <Textarea
        ref={box}
        aria-label={label}
        placeholder={label}
        value={text}
        autoFocus={autoFocus}
        rows={2}
        maxLength={MAX}
        aria-controls={options.length ? listId : undefined}
        aria-autocomplete="list"
        onChange={(e) => {
          setText(e.target.value);
          setCaret(e.target.selectionStart ?? e.target.value.length);
        }}
        onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
        onKeyDown={onKeyDown}
        className="resize-y"
      />
      {options.length > 0 && (
        <ul id={listId} role="listbox" aria-label={t('comments.mentions')} className="max-h-40 overflow-auto rounded-md bg-surface shadow-md">
          {options.map((m, i) => (
            <li
              key={m.userId}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(m);
              }}
              className={`cursor-pointer px-2 py-1 font-sans text-sm ${i === active ? 'bg-accent/10 text-fg' : 'text-fg-muted'}`}
            >
              {m.displayName}
            </li>
          ))}
        </ul>
      )}
      {text.length > MAX - 500 && <span className="font-sans text-xs text-fg-muted">{t('comments.count', { count: text.length })}</span>}
      {failed && (
        <span role="alert" className="font-sans text-xs text-danger">
          {t('comments.error')}
        </span>
      )}
      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button onClick={onCancel}>{t('comments.cancel')}</Button>
        )}
        <Button type="submit" variant="primary" disabled={busy || !text.trim()}>
          {submitLabel ?? t('comments.send')}
        </Button>
      </div>
    </form>
  );
}
